import * as XLSX from 'xlsx';
import { supabase } from '../config/supabase';
import { extractOwnerFromSheetName, getPropertyNameByOwner } from '../config/propertyMapping';

// Tipos
export interface BookingReservation {
  month: string;
  platform: string;
  guestName: string;
  dates: string;
  nights: number;
  pricePerNight: number;
  totalPrice: number;
  inmohostCommission: number;
  inmohostWithVAT: number;
  cleaningCost: number;
  ownerProfit: number;
}

export interface MonthlyAggregation {
  propertyName: string;
  propertyId: string | null;
  year: number;
  month: number;
  monthName: string;
  revenue: number;
  cleaningCosts: number;
  inmohostCosts: number;
  profit: number;
  reservations: number;
  averageNightPrice: number;
  averageStayDuration: number;
  rawReservations: BookingReservation[];
}

export interface ImportPreview {
  propertySheets: {
    sheetName: string;
    propertyName: string;
    propertyId: string | null;
    monthlyData: MonthlyAggregation[];
    totalReservations: number;
    totalRevenue: number;
  }[];
  summary: {
    totalProperties: number;
    totalMonths: number;
    totalReservations: number;
    totalRevenue: number;
  };
  conflicts: ConflictInfo[];
  warnings: string[];
}

export interface ConflictInfo {
  propertyName: string;
  year: number;
  month: number;
  existingData: {
    revenue: number;
    profit: number;
    reservations: number;
  };
  newData: {
    revenue: number;
    profit: number;
    reservations: number;
  };
}

// Mapeo de meses en español a números
const MONTH_MAP: Record<string, number> = {
  'ENERO': 1,
  'FEBRERO': 2,
  'MARZO': 3,
  'ABRIL': 4,
  'MAYO': 5,
  'JUNIO': 6,
  'JULIO': 7,
  'AGOSTO': 8,
  'SEPTIEMBRE': 9,
  'OCTUBRE': 10,
  'NOVIEMBRE': 11,
  'DICIEMBRE': 12,
};

/**
 * Extrae el nombre de la propiedad desde el nombre de la pestaña usando el mapeo
 * Ejemplo: "1. RESERVAS INAKI HARO" → "1880 Haros Old Town House"
 */
function extractPropertyNameFromSheet(sheetName: string): { ownerName: string | null; propertyName: string | null } {
  // Extraer nombre del propietario de la pestaña
  const ownerName = extractOwnerFromSheetName(sheetName);
  
  if (!ownerName) {
    return { ownerName: null, propertyName: null };
  }
  
  // Obtener nombre de la propiedad usando el mapeo
  const propertyName = getPropertyNameByOwner(ownerName);
  
  return { ownerName, propertyName };
}

/**
 * Busca una propiedad en la base de datos por nombre exacto
 */
async function findPropertyByName(propertyName: string): Promise<string | null> {
  try {
    const { data: properties, error } = await supabase
      .from('properties')
      .select('id, name');

    if (error) throw error;
    if (!properties || properties.length === 0) return null;

    // Buscar coincidencia exacta primero
    const exactMatch = properties.find(
      p => p.name.toLowerCase() === propertyName.toLowerCase()
    );
    if (exactMatch) return exactMatch.id;

    // Buscar coincidencia parcial (por si hay pequeñas diferencias)
    const partialMatch = properties.find(p =>
      p.name.toLowerCase().includes(propertyName.toLowerCase()) ||
      propertyName.toLowerCase().includes(p.name.toLowerCase())
    );
    if (partialMatch) return partialMatch.id;

    return null;
  } catch (error) {
    console.error('Error buscando propiedad:', error);
    return null;
  }
}

/**
 * Procesa una fila de reserva del Excel
 */
function parseReservationRow(row: any): BookingReservation | null {
  // Verificar que la fila tiene datos válidos
  // Soportar tanto columnas con nombres como __EMPTY_X
  const totalPrice = Number(row[' PRECIO ESTANCIA'] || row.__EMPTY_7) || 0;
  if (!row || totalPrice === 0) return null;

  try {
    // Convertir month a string de forma segura
    const monthValue = row.__EMPTY || row['__EMPTY'];
    const monthString = monthValue ? String(monthValue).trim() : '';
    
    // Si no hay mes, no es una reserva válida
    if (!monthString) return null;
    
    // Buscar la columna de noches (puede ser "NOCHES", "Nº NOCHES" o __EMPTY_5)
    const nights = Number(row.NOCHES || row['Nº NOCHES'] || row.__EMPTY_5) || 0;
    
    // Buscar la columna de ingreso del propietario (varía por propiedad)
    // Puede ser " INGRESO IÑAKI", " INGRESO GORKA", " INGRESO AGUR", __EMPTY_11, etc.
    let ownerProfit = 0;
    const ingresoKeys = Object.keys(row).filter(key => 
      key.toUpperCase().includes('INGRESO') && 
      !key.toUpperCase().includes('BANCARIO')
    );
    if (ingresoKeys.length > 0) {
      ownerProfit = Number(row[ingresoKeys[0]]) || 0;
    }
    
    // Plataforma: puede estar en PLATAFORMA, " " (espacio) o __EMPTY_3
    const platform = row.PLATAFORMA || row[' '] || '';
    
    return {
      month: monthString,
      platform: platform ? String(platform) : '',
      guestName: row.NOMBRE || row.__EMPTY_3 ? String(row.NOMBRE || row.__EMPTY_3) : '',
      dates: row.RESERVA || row.__EMPTY_4 ? String(row.RESERVA || row.__EMPTY_4) : '',
      nights,
      pricePerNight: Number(row[' PRECIO NOCHE'] || row.__EMPTY_6) || 0,
      totalPrice,
      inmohostCommission: Number(row[' 20% INMOHOST'] || row[' 17% INMOHOST'] || row[' 18% INMOHOST'] || row[' 16% INMOHOST'] || row.__EMPTY_8) || 0,
      inmohostWithVAT: Number(row[' INMOHOST + IVA'] || row.__EMPTY_9) || 0,
      cleaningCost: Number(row[' LIMPIEZA'] || row.__EMPTY_10) || 0,
      ownerProfit,
    };
  } catch (error) {
    console.error('Error parseando fila:', error);
    return null;
  }
}

/**
 * Agrupa reservas por mes
 */
function aggregateByMonth(
  reservations: BookingReservation[],
  propertyName: string,
  propertyId: string | null,
  year: number
): MonthlyAggregation[] {
  const monthlyMap = new Map<string, BookingReservation[]>();

  // Agrupar reservas por mes
  reservations.forEach(reservation => {
    // Validar que month existe y es un string
    if (!reservation.month || typeof reservation.month !== 'string') {
      return; // Saltar esta reserva
    }
    
    const monthName = reservation.month.toUpperCase().trim();
    if (!monthlyMap.has(monthName)) {
      monthlyMap.set(monthName, []);
    }
    monthlyMap.get(monthName)!.push(reservation);
  });

  // Calcular agregaciones
  const aggregations: MonthlyAggregation[] = [];

  monthlyMap.forEach((monthReservations, monthName) => {
    const monthNumber = MONTH_MAP[monthName];
    if (!monthNumber) return; // Ignorar si no es un mes válido

    const revenue = monthReservations.reduce((sum, r) => sum + r.totalPrice, 0);
    const cleaningCosts = monthReservations.reduce((sum, r) => sum + r.cleaningCost, 0);
    const inmohostCosts = monthReservations.reduce((sum, r) => sum + r.inmohostWithVAT, 0);
    const profit = monthReservations.reduce((sum, r) => sum + r.ownerProfit, 0);
    const totalNights = monthReservations.reduce((sum, r) => sum + r.nights, 0);
    const totalPricePerNight = monthReservations.reduce((sum, r) => sum + r.pricePerNight, 0);

    aggregations.push({
      propertyName,
      propertyId,
      year,
      month: monthNumber,
      monthName,
      revenue: Math.round(revenue * 100) / 100,
      cleaningCosts: Math.round(cleaningCosts * 100) / 100,
      inmohostCosts: Math.round(inmohostCosts * 100) / 100,
      profit: Math.round(profit * 100) / 100,
      reservations: monthReservations.length,
      averageNightPrice: monthReservations.length > 0 
        ? Math.round((totalPricePerNight / monthReservations.length) * 100) / 100 
        : 0,
      averageStayDuration: monthReservations.length > 0 
        ? Math.round(totalNights / monthReservations.length) 
        : 0,
      rawReservations: monthReservations,
    });
  });

  return aggregations.sort((a, b) => a.month - b.month);
}

/**
 * Detecta conflictos con datos existentes en la base de datos
 */
async function detectConflicts(
  monthlyData: MonthlyAggregation[]
): Promise<ConflictInfo[]> {
  const conflicts: ConflictInfo[] = [];

  for (const data of monthlyData) {
    if (!data.propertyId) continue;

    try {
      const { data: existing, error } = await supabase
        .from('monthly_stats')
        .select('revenue, profit, reservations')
        .eq('property_id', data.propertyId)
        .eq('year', data.year)
        .eq('month', data.month)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows found (no es error)
        console.error('Error verificando conflictos:', error);
        continue;
      }

      if (existing) {
        conflicts.push({
          propertyName: data.propertyName,
          year: data.year,
          month: data.month,
          existingData: {
            revenue: existing.revenue || 0,
            profit: existing.profit || 0,
            reservations: existing.reservations || 0,
          },
          newData: {
            revenue: data.revenue,
            profit: data.profit,
            reservations: data.reservations,
          },
        });
      }
    } catch (error) {
      console.error('Error detectando conflictos:', error);
    }
  }

  return conflicts;
}

/**
 * Procesa el archivo Excel de reservas y genera un preview
 */
export async function processBookingExcel(
  file: File,
  year: number = new Date().getFullYear()
): Promise<ImportPreview> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });

        const propertySheets: ImportPreview['propertySheets'] = [];
        const warnings: string[] = [];
        let totalReservations = 0;
        let totalRevenue = 0;

        // Filtrar solo las pestañas de propiedades (ignorar GENERAL y FACTURACIÓN)
        const propertySheetNames = workbook.SheetNames.filter(
          name => !name.includes('GENERAL') && !name.includes('FACTURACIÓN')
        );

        // Procesar cada pestaña de propiedad
        for (const sheetName of propertySheetNames) {
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet);

          // Extraer nombre de propietario y propiedad usando el mapeo
          const { ownerName, propertyName } = extractPropertyNameFromSheet(sheetName);

          if (!propertyName) {
            warnings.push(
              `No se pudo mapear la pestaña "${sheetName}" a una propiedad conocida. ` +
              `Propietario detectado: "${ownerName || 'desconocido'}"`
            );
            continue; // Saltar esta pestaña
          }

          // Buscar ID de propiedad en la BD
          const propertyId = await findPropertyByName(propertyName);

          if (!propertyId) {
            warnings.push(
              `No se encontró la propiedad "${propertyName}" en la base de datos. ` +
              `Pestaña: "${sheetName}", Propietario: "${ownerName}"`
            );
            // Continuar procesando aunque no se encuentre en BD (para el preview)
          }

          // Parsear reservas
          const reservations: BookingReservation[] = [];
          rows.forEach((row: any) => {
            const reservation = parseReservationRow(row);
            if (reservation && reservation.totalPrice > 0) {
              reservations.push(reservation);
            }
          });

          // Agregar por mes
          const monthlyData = aggregateByMonth(reservations, propertyName, propertyId, year);

          // Calcular totales de esta propiedad
          const propertyRevenue = monthlyData.reduce((sum, m) => sum + m.revenue, 0);
          const propertyReservations = monthlyData.reduce((sum, m) => sum + m.reservations, 0);

          propertySheets.push({
            sheetName,
            propertyName,
            propertyId,
            monthlyData,
            totalReservations: propertyReservations,
            totalRevenue: propertyRevenue,
          });

          totalReservations += propertyReservations;
          totalRevenue += propertyRevenue;
        }

        // Detectar conflictos con datos existentes
        const allMonthlyData = propertySheets.flatMap(ps => ps.monthlyData);
        const conflicts = await detectConflicts(allMonthlyData);

        resolve({
          propertySheets,
          summary: {
            totalProperties: propertySheets.length,
            totalMonths: allMonthlyData.length,
            totalReservations,
            totalRevenue: Math.round(totalRevenue * 100) / 100,
          },
          conflicts,
          warnings,
        });
      } catch (error: any) {
        reject(new Error(`Error procesando Excel: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error leyendo el archivo'));
    };

    reader.readAsBinaryString(file);
  });
}

/**
 * Importa los datos mensuales a la base de datos
 */
export async function importMonthlyData(
  monthlyData: MonthlyAggregation[],
  conflictResolution: 'skip' | 'update' | 'sum' = 'skip'
): Promise<{ success: number; skipped: number; errors: string[] }> {
  let success = 0;
  let skipped = 0;
  const errors: string[] = [];

  for (const data of monthlyData) {
    if (!data.propertyId) {
      errors.push(
        `Propiedad "${data.propertyName}" no encontrada en la BD. Mes ${data.monthName} omitido.`
      );
      skipped++;
      continue;
    }

    try {
      // Verificar si ya existe
      const { data: existing, error: checkError } = await supabase
        .from('monthly_stats')
        .select('id, revenue, profit, reservations')
        .eq('property_id', data.propertyId)
        .eq('year', data.year)
        .eq('month', data.month)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existing) {
        // Ya existe un registro
        if (conflictResolution === 'skip') {
          skipped++;
          continue;
        } else if (conflictResolution === 'update') {
          // Actualizar registro existente
          const { error: updateError } = await supabase
            .from('monthly_stats')
            .update({
              revenue: data.revenue,
              cleaning_costs: data.cleaningCosts,
              inmohost_costs: data.inmohostCosts,
              profit: data.profit,
              reservations: data.reservations,
              average_night_price: data.averageNightPrice,
              average_stay_duration: data.averageStayDuration,
              occupancy_rate: 0, // No disponible en Excel
            })
            .eq('id', existing.id);

          if (updateError) throw updateError;
          success++;
        } else if (conflictResolution === 'sum') {
          // Sumar a los valores existentes
          const { error: updateError } = await supabase
            .from('monthly_stats')
            .update({
              revenue: existing.revenue + data.revenue,
              profit: existing.profit + data.profit,
              reservations: existing.reservations + data.reservations,
            })
            .eq('id', existing.id);

          if (updateError) throw updateError;
          success++;
        }
      } else {
        // Crear nuevo registro
        const { error: insertError } = await supabase
          .from('monthly_stats')
          .insert({
            property_id: data.propertyId,
            year: data.year,
            month: data.month,
            revenue: data.revenue,
            cleaning_costs: data.cleaningCosts,
            inmohost_costs: data.inmohostCosts,
            profit: data.profit,
            reservations: data.reservations,
            average_night_price: data.averageNightPrice,
            average_stay_duration: data.averageStayDuration,
            occupancy_rate: 0, // No disponible en Excel
          });

        if (insertError) throw insertError;
        success++;
      }
    } catch (error: any) {
      errors.push(
        `Error en ${data.propertyName} - ${data.monthName}: ${error.message}`
      );
    }
  }

  return { success, skipped, errors };
}
